import React, { useState, useMemo } from 'react';
import { ArrowLeft, Search, CheckCircle, AlertTriangle, XCircle, HelpCircle, Clock, AlertCircle, ExternalLink, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { AddressValidationResult, ValidationStatus } from '../../types/ppcFarmTypes';

interface AddressValidationPageProps {
  validationResults: AddressValidationResult[];
  onBack: () => void;
  isLoading?: boolean;
}

const statusConfig = {
  VALIDATED: {
    label: 'Validated',
    icon: CheckCircle,
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    description: 'Addresses that are fully validated and deliverable'
  },
  NEEDS_CONFIRMATION: {
    label: 'Needs Confirmation',
    icon: AlertTriangle,
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    description: 'Addresses that need manual review or confirmation'
  },
  NEEDS_FIXING: {
    label: 'Needs Fixing',
    icon: AlertCircle,
    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    description: 'Addresses with issues that can be corrected'
  },
  MISSING_UNIT: {
    label: 'Missing Unit',
    icon: HelpCircle,
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    description: 'Addresses missing apartment or unit numbers'
  },
  UNVALIDATED: {
    label: 'Unvalidated',
    icon: Clock,
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    description: 'Addresses that have not been processed yet'
  },
  ERROR: {
    label: 'Error',
    icon: XCircle,
    color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    description: 'Addresses that encountered validation errors'
  }
};

// Sort configuration for address validation results
interface SortConfig {
  field: 'sellerName' | 'businessName' | 'sellerId' | 'originalAddress' | 'validatedAddress' | 'status';
  direction: 'asc' | 'desc';
}

export const AddressValidationPage: React.FC<AddressValidationPageProps> = ({
  validationResults,
  onBack,
  isLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<ValidationStatus>('VALIDATED');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'sellerName',
    direction: 'asc'
  });

  // Helper functions (moved above useMemo to avoid hoisting issues)
  const formatOriginalAddress = (address: { line1?: string; city?: string; state?: string; zip?: string; country?: string; } | undefined) => {
    if (!address) return 'N/A';
    const parts = [address.line1, address.city, address.state, address.zip].filter(Boolean);
    const fullAddress = parts.join(', ');
    return fullAddress.length > 60 ? `${fullAddress.substring(0, 60)}...` : fullAddress;
  };

  const formatStandardizedAddress = (address: { line1: string; line2?: string; city: string; state: string; zipCode5: string; zipCode4?: string; } | undefined) => {
    if (!address) return 'N/A';
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.state,
      address.zipCode4 ? `${address.zipCode5}-${address.zipCode4}` : address.zipCode5
    ].filter(Boolean);
    const fullAddress = parts.join(', ');
    return fullAddress.length > 60 ? `${fullAddress.substring(0, 60)}...` : fullAddress;
  };

  // Group results by status
  const resultsByStatus = useMemo(() => {
    const grouped = validationResults.reduce((acc, result) => {
      const status = (result.status || 'UNVALIDATED') as ValidationStatus;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(result);
      return acc;
    }, {} as Record<ValidationStatus, AddressValidationResult[]>);

    // Ensure all status categories exist
    Object.keys(statusConfig).forEach(status => {
      if (!grouped[status as ValidationStatus]) {
        grouped[status as ValidationStatus] = [];
      }
    });

    return grouped;
  }, [validationResults]);

  // Filter and sort results based on search term and sort configuration
  const filteredAndSortedResults = useMemo(() => {
    let results = resultsByStatus[activeTab] || [];

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      results = results.filter(result => {
        const originalAddressStr = formatOriginalAddress(result.originalAddress);
        const standardizedAddressStr = result.standardizedAddress ?
          `${result.standardizedAddress.line1} ${result.standardizedAddress.city} ${result.standardizedAddress.state} ${result.standardizedAddress.zipCode5}` : '';
        const businessName = result.businessName || '';
        
        return (
          result.sellerName?.toLowerCase().includes(term) ||
          businessName.toLowerCase().includes(term) ||
          originalAddressStr.toLowerCase().includes(term) ||
          standardizedAddressStr.toLowerCase().includes(term) ||
          result.sellerId?.toLowerCase().includes(term)
        );
      });
    }

    // Apply sorting
    return [...results].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.field) {
        case 'sellerName':
          aValue = a.sellerName || '';
          bValue = b.sellerName || '';
          break;
        case 'businessName':
          aValue = a.businessName || '';
          bValue = b.businessName || '';
          break;
        case 'sellerId':
          aValue = a.sellerId || '';
          bValue = b.sellerId || '';
          break;
        case 'originalAddress':
          aValue = formatOriginalAddress(a.originalAddress);
          bValue = formatOriginalAddress(b.originalAddress);
          break;
        case 'validatedAddress':
          aValue = a.standardizedAddress ? formatStandardizedAddress(a.standardizedAddress) : '';
          bValue = b.standardizedAddress ? formatStandardizedAddress(b.standardizedAddress) : '';
          break;
        case 'status':
          aValue = a.status || 'UNVALIDATED';
          bValue = b.status || 'UNVALIDATED';
          break;
        default:
          aValue = '';
          bValue = '';
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      return 0;
    });
  }, [resultsByStatus, activeTab, searchTerm, sortConfig, formatOriginalAddress, formatStandardizedAddress]);

  // Handle sort column click
  const handleSort = (field: SortConfig['field']) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Render sort icon
  const renderSortIcon = (field: SortConfig['field']) => {
    if (sortConfig.field !== field) {
      return <ChevronUp className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="h-4 w-4 text-blue-600" />
      : <ChevronDown className="h-4 w-4 text-blue-600" />;
  };

  // Open seller page in new tab
  const openSellerPage = (sellerId: string) => {
    if (!sellerId) return;
    const url = `https://www.amazon.com/sp?seller=${sellerId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getStatusBadge = (status: ValidationStatus) => {
    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return null;
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <Button variant="ghost" onClick={onBack} className="mr-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Review
          </Button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            Address Validation
          </h1>
        </div>
        
        <Card>
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading validation results...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Button variant="ghost" onClick={onBack} className="mr-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Review
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              Address Validation Results
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {validationResults.length} addresses processed
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by seller name, business name, address, or seller ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ValidationStatus)}>
        <TabsList className="grid w-full grid-cols-6 mb-6">
          {Object.entries(statusConfig).map(([status, config]) => {
            const count = resultsByStatus[status as ValidationStatus]?.length || 0;
            const Icon = config.icon;
            return (
              <TabsTrigger key={status} value={status} className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{config.label}</span>
                <Badge variant="secondary" className="ml-1">
                  {count}
                </Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(statusConfig).map(([status, config]) => (
          <TabsContent key={status} value={status}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <config.icon className="h-5 w-5" />
                  {config.label} ({filteredAndSortedResults.length})
                </CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {config.description}
                </p>
              </CardHeader>
              <CardContent>
                {filteredAndSortedResults.length === 0 ? (
                  <div className="text-center py-8">
                    <config.icon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                      {searchTerm ? 'No addresses match your search criteria.' : `No addresses in ${config.label.toLowerCase()} status.`}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th
                            className="text-left p-3 font-medium cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                            onClick={() => handleSort('sellerName')}
                          >
                            <div className="flex items-center">
                              Seller Name
                              {renderSortIcon('sellerName')}
                            </div>
                          </th>
                          <th
                            className="text-left p-3 font-medium cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                            onClick={() => handleSort('businessName')}
                          >
                            <div className="flex items-center">
                              Business Name
                              {renderSortIcon('businessName')}
                            </div>
                          </th>
                          <th
                            className="text-left p-3 font-medium cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                            onClick={() => handleSort('sellerId')}
                          >
                            <div className="flex items-center">
                              Seller ID
                              {renderSortIcon('sellerId')}
                            </div>
                          </th>
                          <th
                            className="text-left p-3 font-medium cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                            onClick={() => handleSort('originalAddress')}
                          >
                            <div className="flex items-center">
                              Original Address
                              {renderSortIcon('originalAddress')}
                            </div>
                          </th>
                          <th
                            className="text-left p-3 font-medium cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                            onClick={() => handleSort('validatedAddress')}
                          >
                            <div className="flex items-center">
                              Validated Address
                              {renderSortIcon('validatedAddress')}
                            </div>
                          </th>
                          <th
                            className="text-left p-3 font-medium cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                            onClick={() => handleSort('status')}
                          >
                            <div className="flex items-center">
                              Status
                              {renderSortIcon('status')}
                            </div>
                          </th>
                          <th className="text-left p-3 font-medium">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAndSortedResults.map((result, index) => (
                          <tr key={`${result.sellerId}-${index}`} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="p-3">
                              <div className="font-medium text-sm flex items-center gap-2">
                                <span>{result.sellerName || 'N/A'}</span>
                                {result.sellerId && (
                                  <button
                                    onClick={() => openSellerPage(result.sellerId)}
                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                                    title="View seller page on Amazon"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                {result.businessName || 'N/A'}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="text-sm font-mono">
                                {result.sellerId}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="text-sm max-w-xs">
                                {formatOriginalAddress(result.originalAddress)}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="text-sm max-w-xs">
                                {result.standardizedAddress ? (
                                  <span className="text-green-600 dark:text-green-400">
                                    {formatStandardizedAddress(result.standardizedAddress)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">Not available</span>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              {getStatusBadge((result.status || 'UNVALIDATED') as ValidationStatus)}
                            </td>
                            <td className="p-3">
                              <div className="text-xs text-gray-600 dark:text-gray-400 max-w-xs">
                                {result.statusReason || result.errorMessage || 'No details available'}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};